import { memo, useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { type Group, Vector3 } from 'three';
import { type ThreeEvent, useThree, invalidate } from '@react-three/fiber';
import { DragControls } from 'components/DragControls';
import { useDispatch, useSelector } from 'app/store';
import { useApplicationStore } from 'modules/application/store';
import { activeElementActivateLink } from 'modules/activeElement/slice';
import { devicesBottomPanelImgSrcSelector, devicesDeviceDefaultInstanceSelector } from 'modules/devices/slice';
import { Z } from 'modules/editor/constants';
import { useStaticSize } from 'modules/editor/hooks/useStaticSize';
import { useEditorStore, type State } from 'modules/editor/store';
import {
  linksLinkIdsByRadioIdSelector,
  linksLinkSignalValueSelector,
  linksUplinkIdByClientIdSelector,
} from 'modules/links/slice';
import { trackEventDebounced } from 'utils/googleAnalytics';
import { getSingleImgSrc } from 'utils/imageUtils';
import { useImageSupport } from 'hooks/useImageSupport';

import { settingsShowDeviceNameSelector } from 'modules/settings/slice';
import { CLIENT_ID, MESH_AP_ID } from 'modules/devices/constants';
import { Device as DeviceView } from '../components/Device';

type Props = {
  id: string;
  isActive: boolean;
  onDrag?: (position: Vector3, id: string) => void;
  onDragEnd?: (position: Vector3, id: string) => void;
};

const positionVector = new Vector3();
const pointerVector = new Vector3();
let prevEditorState: State['state'];
let tooltipTimeout: ReturnType<typeof setTimeout>;
const dragThreshold = 0.1;
const wasDragged = () =>
  [positionVector.x - pointerVector.x, positionVector.y - pointerVector.y].some(
    (diff) => Math.abs(diff) > dragThreshold
  );

function DeviceImpl(props: Props) {
  const { id, isActive, onDrag, onDragEnd } = props;

  const [dragging, setDragging] = useState(false);
  const isMesh = id === MESH_AP_ID;
  const isClient = id === CLIENT_ID;

  const deviceLinkId = useSelector((state) => linksLinkIdsByRadioIdSelector(state, id)[0]);
  const clientLinkId = useSelector((state) => linksUplinkIdByClientIdSelector(state, id));
  const showDeviceName = useSelector(settingsShowDeviceNameSelector);
  const deviceName = useSelector((state) => devicesDeviceDefaultInstanceSelector(state, id).displayName);
  const signalPercentage = useSelector((state) =>
    clientLinkId ? linksLinkSignalValueSelector(state, clientLinkId) : undefined
  );

  const { isLoading, supportsAvif, supportsWebp } = useImageSupport();
  const imageUrls = useSelector((state) => devicesBottomPanelImgSrcSelector(state, id));
  const imageUrl = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    return getSingleImgSrc(imageUrls, supportsAvif, supportsWebp);
  }, [imageUrls, isLoading, supportsAvif, supportsWebp]);

  const dispatch = useDispatch();

  const isTouchDevice = useApplicationStore((state) => state.isTouchDevice);
  const setDevicePosition = useEditorStore((state) => state.setDevicePosition);
  const hoverDeviceId = useEditorStore((state) => state.hoverDeviceId);
  const domElement = useThree((state) => state.gl.domElement);

  const ref = useRef<Group>(null);

  const deviceRef = useRef<Group>(null);
  useStaticSize(deviceRef);

  const getPosition = useCallback((vector?: Vector3) => {
    const position = vector ?? positionVector;
    if (!ref.current) {
      return position;
    }
    return position.setFromMatrixPosition(ref.current.matrixWorld);
  }, []);

  // set initial position on mount
  useEffect(() => {
    if (!ref.current) {
      return;
    }

    ref.current.position.set(...useEditorStore.getState().devices[id].position);
    ref.current.updateMatrix();
  }, [id, imageUrl]); // imageUrl is used to trigger re-render when ref becomes available

  // update render order when active/dragging/hover states change
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    if (dragging) {
      ref.current.position.z = Z.DRAGGING;
    } else if (hoverDeviceId === id) {
      ref.current.position.z = isActive ? Z.HOVER_ACTIVE : Z.HOVER;
    } else {
      ref.current.position.z = isActive ? Z.ACTIVE : Z.DEFAULT;
    }
    invalidate();
  }, [isActive, hoverDeviceId, dragging, id]);

  const showContextMenu = useCallback(() => {
    if (isMesh) {
      useEditorStore.getState().updateContextMenu({
        open: true,
        position: getPosition().clone(),
        id,
      });
    }
  }, [getPosition, id, isMesh]);

  const onClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      e.stopPropagation();
      showContextMenu();
    },
    [showContextMenu]
  );

  const onPointerDown = useCallback(() => {
    getPosition(pointerVector);
    dispatch(activeElementActivateLink(clientLinkId ?? deviceLinkId));
  }, [clientLinkId, deviceLinkId, dispatch, getPosition]);

  const onPointerOver = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      getPosition();
      getPosition(pointerVector);

      if (isTouchDevice || useEditorStore.getState().state === 'dragging') {
        return;
      }

      showContextMenu();
      clearTimeout(tooltipTimeout);
      useEditorStore.setState({ hoverDeviceId: id, showTooltip: isClient });
      domElement.style.cursor = 'pointer';
    },
    [getPosition, isTouchDevice, domElement.style, id, isClient, showContextMenu]
  );

  const onPointerOut = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();

      if (isTouchDevice || useEditorStore.getState().state === 'dragging' || (wasDragged() && window.safari)) {
        return;
      }

      useEditorStore.setState({ hoverDeviceId: null });
      tooltipTimeout = setTimeout(() => useEditorStore.setState({ showTooltip: false }), 50);
      domElement.style.cursor = 'auto';
    },
    [domElement.style, isTouchDevice]
  );

  // Note: on mobile dragStart is fired before onPointerOver
  // Note2: in niche scenarios dragStart can fire without onPointerOver firing at all even on desktop
  const handleDragStart = useCallback(() => {
    prevEditorState = useEditorStore.getState().state;
    // prioritize client on mobile while 2 fingers on screen
    const hoverId = hoverDeviceId === CLIENT_ID ? hoverDeviceId : id;
    useEditorStore.setState({ state: 'dragging', hoverDeviceId: hoverId });
    useEditorStore.getState().updateContextMenu({ open: false, id: null });

    if (isTouchDevice && isClient) {
      useEditorStore.setState({ showTooltip: true });
    } else {
      domElement.style.cursor = 'grabbing';
    }

    setDragging(true);
  }, [domElement.style, id, isClient, isTouchDevice, hoverDeviceId]);

  const handleDrag = useCallback(() => {
    const position = getPosition();
    setDevicePosition(id, position.toArray());
    onDrag?.(position, id);
  }, [getPosition, setDevicePosition, id, onDrag]);

  const handleDragEnd = useCallback(() => {
    const position = getPosition();
    setDevicePosition(id, position.toArray());
    onDragEnd?.(position, id);

    useEditorStore.setState({ state: prevEditorState });

    if (isTouchDevice) {
      useEditorStore.setState({ showTooltip: false, hoverDeviceId: null });
    } else {
      domElement.style.cursor = 'pointer';
    }

    setDragging(false);
    trackEventDebounced('canvasMoveDevice', { id });
  }, [domElement.style, getPosition, id, isTouchDevice, onDragEnd, setDevicePosition]);

  if (!imageUrl) {
    return null;
  }

  return (
    <DragControls onDragStart={handleDragStart} onDrag={handleDrag} onDragEnd={handleDragEnd} ref={ref}>
      <DeviceView
        ref={deviceRef}
        imageUrl={imageUrl}
        deviceName={showDeviceName ? deviceName : undefined}
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerOver={onPointerOver}
        onPointerOut={onPointerOut}
        signalPercentage={signalPercentage}
      />
    </DragControls>
  );
}

export const Device = memo(DeviceImpl);
